import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { assert } from '@ember/debug';
import { isNone } from '@ember/utils';
import type IntlService from 'ember-intl/services/intl';

export interface AditoolsDialogLoadSignature {
  Element: HTMLDialogElement;
  Args: {
    title: string;
    load: (design: any) => void;
    close: () => void;
    chooseFileButtonText?: string;
    closeButtonText?: string;
    loadButtonText?: string;
    dialogClass?: string;
    submit?: () => void;
  };
}
type AditoolsDialogLoadArgs = AditoolsDialogLoadSignature['Args'];
export default class AditoolsDialogLoad extends Component<AditoolsDialogLoadSignature> {
  supportsDataTestProperties = true;
  @service declare intl: IntlService;
  @tracked fileName = ''; // Name of file to be uploaded
  @tracked designData: any = null; // Design Data to be uploaded
  fileObject: any = null; // File to be uploaded
  @tracked uploadDisabled = true; // Flag that Enables/Disables Load Button

  constructor(owner: unknown, args: AditoolsDialogLoadArgs) {
    super(owner, args);
    assert('`title` is required', !isNone(this.args.title));
    if (!isNone(this.args.submit)) {
      console.log(`
            The \`submit\` parameter has changed to \`load\`.
            Please adjust your usage accordingly.
            `);
    }
    assert('`load` is required', !isNone(this.args.load));
    assert('`close` is required', !isNone(this.args.close));
  }

  // Optional parameters with defaults applied
  get _chooseFileButtonText() {
    return this.args.chooseFileButtonText ?? this.intl.t('ember-aditools-page.dialogs.load.choose-button');
  }

  get _loadButtonText() {
    return this.args.loadButtonText ?? this.intl.t('ember-aditools-page.common.load');
  }

  get _closeButtonText() {
    return this.args.closeButtonText ?? this.intl.t('ember-aditools-page.common.cancel');
  }

  get _fileName() {
    return this.fileName && this.fileName.length > 0
      ? this.fileName
      : this.intl.t('ember-aditools-page.dialogs.load.no-file-selected');
  }

  @action chooseFile(event: MouseEvent) {
    const target = event.currentTarget as HTMLElement; //Safe because we are setting the handlers on an HTML element
    const rootForSelection = target.parentElement; // Limit the scope of the lookup to keep it inside this component
    if (rootForSelection) {
      const designFileUpload = rootForSelection.querySelectorAll('#design-file-upload')[0] as HTMLElement;
      designFileUpload.click();
    }
  }
  // Event Handler when the user selects a file from the Directory/Explorer Dialog
  @action getFileInfo({ target }: { target: any }) {
    const files = target.files;
    const [file] = files;
    const fileobj = files && file ? file : null;
    this.fileObject = fileobj;
    this.fileName = files && file ? file.name : '';
    this.uploadDisabled = fileobj === null;
  }
  // Event Handler when the user selects a file from the Directory/Explorer Dialog
  @action loadFile(closeDialog: () => void) {
    const selfRef = this; // eslint-disable-line @typescript-eslint/no-this-alias
    if (this.fileObject) {
      const reader = new FileReader();
      reader.onload = function (e) {
        if (!e) {
          selfRef.designData = reader.result;
        } else if (e.target) {
          selfRef.designData = e.target.result;
        }
        if (selfRef.args.load) {
          selfRef.args.load(selfRef.designData);
        }
        closeDialog();
      };
      //@ts-ignore
      reader.readAsText(this.fileObject, 'utf-8');
    }
  }
}
